import { RadioButton } from '@components/form/baseInputs'
import {
  AddressLookup,
  AgreementNo,
  Button,
  DateMask,
  DateOfBirthMask,
  Email,
  FullName,
  Tel,
  TextInput,
  VehicleRegNo,
  YesNo,
} from '@components/form/fields'
import { Form, FormStep, FormWrapper } from '@components/form/layout'
import { Nav } from '@components/ui'
import { yupResolver } from '@hookform/resolvers/yup'
import { ERROR_MESSAGE } from '@utils/constants'
import closeOverlay from '@utils/helpers/closeOverlay'
import { getContentFile, getPageData } from '@utils/helpers/index-server'
import { formEvent } from '@utils/helpers/tracking'
import { DiscretionaryCommissionSchema } from '@utils/schemas'
import axios from 'axios'
import { marked } from 'marked'
import type { GetServerSideProps } from 'next'
import { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

const DiscretionaryCommission = ({ content, tracking, brand }) => {
  const [submitResponse, setSubmitResponse] = useState('')
  const [showPrevAddress, setShowPrevAddress] = useState(false)
  const initialValues = {
    form_name: 'discretionary-commission',
    enquiry_type: '',
    full_name: '',
    email: '',
    tel: '',
    dob: '',
    address_line1: '',
    address_line2: '',
    town: '',
    county: '',
    postcode: '',
    same_address: '',
    prev_address_line1: '',
    prev_address_line2: '',
    prev_town: '',
    prev_county: '',
    prev_postcode: '',
    reg_num: '',
    vehicle: '',
    bought: '',
    agreement_or_reg_num: '',
    dealer: '',
    is_lexus: brand === 'lexus',
  }

  const methods = useForm({
    resolver: yupResolver(DiscretionaryCommissionSchema),
    defaultValues: initialValues,
    mode: 'onTouched',
  })

  useEffect(() => {
    const isSameAddress = methods.getValues('same_address') === 'false'
    setShowPrevAddress(isSameAddress)
    if (!isSameAddress) {
      methods.resetField('prev_address_line1')
      methods.resetField('prev_address_line2')
      methods.resetField('prev_town')
      methods.resetField('prev_county')
      methods.resetField('prev_postcode')
    }
  }, [methods.watch('same_address')])

  const onSubmit = async (data) => {
    setSubmitResponse('')
    // If enquiry_type is selected, user has implicitly accepted consent
    const submitData = {
      ...data,
      accept: !!data.enquiry_type,
    }
    await axios({
      method: 'post',
      url: `${process.env.NEXT_PUBLIC_API}/finance`,
      headers: {
        'Content-Type': 'application/json',
      },
      data: submitData,
    })
      .then(() => {
        formEvent(
          {
            conversion: 1,
            name: tracking.form,
            step: 'form-submit',
            variant: '',
            page: tracking.page,
          },
          brand,
          data.email,
        )
        closeOverlay(
          content.confirmation.title,
          content.confirmation.copy,
          setSubmitResponse,
          brand === 'lexus',
        )
      })
      .catch(() => {
        formEvent(
          {
            conversion: 0,
            name: tracking.form,
            step: 'form-error',
            variant: '',
            page: tracking.page,
          },
          brand,
        )
        setSubmitResponse(ERROR_MESSAGE)
        throw new Error(ERROR_MESSAGE)
      })
  }

  return (
    <>
      <Nav
        title={content.title}
        logoType={content?.logotype}
        customLogo={content?.customlogo}
      />
      <FormWrapper>
        <FormStep subtitle={content.details.subtitle} active={true}>
          <FormProvider {...methods}>
            <Form onSubmit={methods.handleSubmit(onSubmit)}>
              <h3 className="text-xl md:text-2xl lg:text-3xl mb-3 !leading-snug">
                Your Consent
              </h3>
              <div className="mb-4">
                <div
                  className="mb-5 [&>p>a]:underline [&>ul]:list-disc [&>ul]:pl-5 [&>ul]:my-2"
                  dangerouslySetInnerHTML={{
                    __html: marked.parse(content.details.enquiry_header, {
                      mangle: false,
                      headerIds: false,
                    }),
                  }}
                ></div>
                <p className="mt-5 mb-3">
                  Please tick one of the below options:
                </p>
                <div className="space-y-2 enquiry-options">
                  <RadioButton
                    name="enquiry_type"
                    label="I am enquiring to find out about possible motor commissions related to my finance agreement."
                    value="enquiry"
                  />
                  <RadioButton
                    name="enquiry_type"
                    label="I am making a complaint about motor commissions."
                    value="complaint"
                  />
                </div>
                <h3 className="text-xl md:text-2xl lg:text-3xl !leading-snug pt-4">
                  Your Details
                </h3>
              </div>
              <FullName />
              <Email />
              <Tel />
              <DateOfBirthMask />
              <AddressLookup optionsLabel="Please select your address" />
              <YesNo
                name="same_address"
                label="Is your address the same as when you took out the motor finance agreement you are enquiring or making a complaint about?"
              />
              {showPrevAddress && (
                <AddressLookup
                  optionsLabel="Please select your previous address"
                  prefix="prev_"
                  label="Previous Postcode"
                />
              )}
              <div className="pt-3 md:pt-4">
                <p>
                  Providing as much information as possible will help us to
                  investigate your enquiry or complaint quickly and avoid
                  delays.
                </p>
              </div>
              <h3 className="text-xl md:text-2xl lg:text-3xl mb-3 !leading-snug pt-4 md:pt-5">
                Vehicle and Agreement Details
              </h3>
              <VehicleRegNo />
              <TextInput name="vehicle" label="Make and model of the vehicle" />
              <AgreementNo label="Agreement number" optional />
              <DateMask
                name="bought"
                label="Date you bought the vehicle"
                optional
              />
              <TextInput
                name="dealer"
                label="Name of the dealership you bought it from"
                optional
              />
              <div className="mt-8">
                <p className="mb-8 text-pretty">
                  Please note, if we cannot find an agreement or the relevant
                  information about motor commission with the details you have
                  provided, we may need to contact you to request further
                  information.
                </p>
              </div>
              <Button submitResponse={submitResponse}>
                {content.details.submit}
              </Button>
            </Form>
          </FormProvider>
        </FormStep>
      </FormWrapper>
    </>
  )
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const brand = context.locale
  const form = 'discretionary-commission'
  const [content, settings] = getContentFile(brand, `forms/${form}`)

  // if no content/disabled - show 404
  if (!content || !content.attributes?.active) {
    return {
      notFound: true,
    }
  }

  // if content property 'hold' or settings property 'maintenance' is true, redirect to holding page
  if (content.attributes?.hold || settings.attributes.maintenance) {
    return {
      redirect: {
        destination: '/maintenance',
        permanent: false,
      },
    }
  }

  return {
    props: {
      content: content.attributes,
      title: content.attributes.title,
      tracking: {
        page: content.attributes.tracking?.page,
        form: content.attributes.tracking?.form,
        variant: '',
        initialStep: 'contact-details',
      },
      description: '',
      ...(await getPageData(context)),
    },
  }
}

export default DiscretionaryCommission
